import React, { useState } from 'react';
import Modal from '../admin/Modal';
import { api } from '../../lib/api';

const SCHOOL_ROLES = [
  ['principal', 'Principal'],
  ['assistant_principal', 'Assistant principal'],
  ['parent_coordinator', 'Parent coordinator'],
  ['purchase_secretary', 'Purchase secretary'],
  ['it_staff', 'IT staff'],
  ['superintendent', 'Superintendent'],
  ['other_school', 'Other (school)'],
];
const EXTERNAL_ROLES = [
  ['owner', 'Owner'],
  ['executive', 'Executive'],
  ['technical_lead', 'Technical lead'],
  ['billing', 'Billing'],
  ['other_external', 'Other (external)'],
];
const CHANNELS = [
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['in_person', 'In person'],
  ['sms', 'SMS'],
];

export default function ContactForm({
  open, onClose, accountId, accountType = 'school', initial = null, onSaved,
}) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    role: initial?.role || (accountType === 'external' ? 'owner' : 'principal'),
    email: initial?.email || '',
    phone: initial?.phone || '',
    isPrimary: !!initial?.isPrimary,
    isDecisionMaker: !!initial?.isDecisionMaker,
    preferredChannel: initial?.preferredChannel || 'email',
    notes: initial?.notes || '',
    roleTitle: initial?.roleTitle || '',
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const roles = accountType === 'external' ? EXTERNAL_ROLES : SCHOOL_ROLES;
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        isPrimary: form.isPrimary,
        isDecisionMaker: form.isDecisionMaker,
        preferredChannel: form.preferredChannel,
        notes: form.notes.trim() || null,
        roleTitle: form.roleTitle.trim() || null,
      };
      const res = isEdit
        ? await api.put(`/api/admin/contacts/${initial.id}`, payload)
        : await api.post(`/api/admin/accounts/${accountId}/contacts`, payload);
      onSaved?.(res.contact);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit contact' : 'New contact'}>
      <div className="space-y-4">
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
        <Field label="Name">
          <input autoFocus className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Role">
          <select className={inputCls} value={form.role} onChange={(e) => set('role', e.target.value)}>
            {roles.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
          </select>
        </Field>
        {form.role.startsWith('other_') && (
          <Field label="Exact role (optional)">
            <input
              className={inputCls}
              value={form.roleTitle}
              onChange={(e) => set('roleTitle', e.target.value)}
              placeholder="e.g. School Business Manager"
              maxLength={80}
              data-testid="contact-role-title"
            />
          </Field>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Preferred channel">
            <select className={inputCls} value={form.preferredChannel} onChange={(e) => set('preferredChannel', e.target.value)}>
              {CHANNELS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
          </Field>
          <div className="flex items-end gap-4 pb-2">
            <Toggle checked={form.isPrimary} onChange={(v) => set('isPrimary', v)} label="Primary" />
            <Toggle checked={form.isDecisionMaker} onChange={(v) => set('isDecisionMaker', v)} label="Decision maker" />
          </div>
        </div>
        <Field label="Notes">
          <textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !form.name.trim()}
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create contact'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      {children}
    </label>
  );
}
function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 select-none cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#B0FC38]" />
      {label}
    </label>
  );
}
